'use client';

import { useState } from 'react';
import {
    ShieldAlert,
    Lock,
    Users,
    Send,
    Loader2,
    CheckCircle2,
    LogOut,
    AlertCircle
} from 'lucide-react';
import toast from 'react-hot-toast';

interface SuspensionModalProps {
    isOpen: boolean;
    operatorId?: string;
    username?: string;
    onLogout?: () => void;
}

export default function SuspensionModal({
    isOpen,
    operatorId,
    username,
    onLogout
}: SuspensionModalProps) {
    const [reason, setReason] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [requestSuccess, setRequestSuccess] = useState<any | null>(null);

    if (!isOpen) return null;

    const handleSubmitReactivation = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!reason.trim()) {
            toast.error('Debe justificar el motivo formal de la reactivación.');
            return;
        }

        setIsSubmitting(true);
        const notifyId = toast.loading('Enviando solicitud a la Mesa de Control Dual...');

        try {
            const res = await fetch('/api/master/security/reactivations/request', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    target_operator_id: operatorId,
                    action_type: 'REACTIVATE_OPERATOR',
                    reason: reason.trim()
                })
            });

            const data = await res.json();
            if (!res.ok || !data.success) {
                throw new Error(data.error || 'Error al iniciar solicitud');
            }

            toast.success('Solicitud de Control Dual registrada.', { id: notifyId });
            setRequestSuccess(data.data);
        } catch (err: any) {
            toast.error(err.message || 'Error en operación', { id: notifyId });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleExecuteLogout = async () => {
        try {
            await fetch('/api/auth/logout', { method: 'POST' });
        } catch (e) {
            // Ignore
        }
        if (onLogout) {
            onLogout();
        } else {
            window.location.href = '/master/login';
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/85 backdrop-blur-xl animate-in fade-in duration-300 font-sans">
            <div className="bg-white border border-rose-200 rounded-3xl p-8 max-w-lg w-full relative shadow-2xl text-slate-900">
                <div className="flex items-center gap-3.5 mb-6">
                    <div className="size-12 rounded-2xl bg-rose-50 border border-rose-200 flex items-center justify-center text-rose-600 shadow-xs">
                        <Lock className="size-6" />
                    </div>
                    <div>
                        <div className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-rose-100 text-rose-800 text-[10px] font-black uppercase tracking-wider mb-1">
                            Bloqueo Preventivo CNV RG 1058/25
                        </div>
                        <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">
                            Cuenta de Operador Suspendida
                        </h3>
                    </div>
                </div>

                <div className="space-y-4 text-xs text-slate-600 leading-relaxed">
                    <p>
                        El plazo de gracia de <strong>7 días corridos</strong> otorgado para configurar una medida obligatoria de segundo factor (Authenticator TOTP o Passkey FIDO2) ha expirado sin registrarse ningún factor de seguridad.
                    </p>
                    <p className="bg-rose-50 p-3.5 rounded-2xl border border-rose-200 text-rose-900 text-[11px] font-medium">
                        Por disposición regulatoria de la Comisión Nacional de Valores y la Unidad de Información Financiera, esta cuenta ha sido bloqueada de forma preventiva para salvaguardar la custodia de activos.
                    </p>

                    {!requestSuccess ? (
                        <form onSubmit={handleSubmitReactivation} className="space-y-4 pt-2">
                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-wider text-slate-700 mb-1.5 flex items-center gap-1.5">
                                    <Users className="size-3.5 text-blue-600" />
                                    Solicitar Reactivación a Mesa de Control Dual (Principio de 4 Ojos)
                                </label>
                                <textarea
                                    required
                                    rows={3}
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    placeholder="Indique motivo fundado de reactivación (ej. Pérdida de dispositivo, solicitud formal de Directorio, cambio de estación de trabajo)..."
                                    className="w-full liquid-glass-input-light rounded-2xl p-3.5 text-xs text-slate-900 outline-none resize-none border border-slate-300 focus:border-blue-500 font-sans"
                                />
                                <span className="text-[10px] text-slate-400 mt-1 block">
                                    Un segundo SuperAdmin independiente deberá aprobar esta solicitud firmando con su propia Passkey biométrica.
                                </span>
                            </div>

                            <div className="flex items-center justify-between pt-2">
                                <button
                                    type="button"
                                    onClick={handleExecuteLogout}
                                    className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-xs font-bold text-slate-600 transition-colors flex items-center gap-1.5 cursor-pointer"
                                >
                                    <LogOut className="size-3.5" />
                                    Cerrar Sesión
                                </button>

                                <button
                                    type="submit"
                                    disabled={isSubmitting || !reason.trim()}
                                    className="px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-black text-xs uppercase tracking-wider transition-all shadow-md flex items-center gap-2 cursor-pointer disabled:opacity-50"
                                >
                                    {isSubmitting ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-3.5" />}
                                    Enviar a Control Dual
                                </button>
                            </div>
                        </form>
                    ) : (
                        <div className="p-4 rounded-2xl bg-emerald-50 border border-emerald-200 space-y-3 animate-in fade-in duration-300">
                            <div className="flex items-center gap-2 text-emerald-800 font-bold text-xs">
                                <CheckCircle2 className="size-4 text-emerald-600 shrink-0" />
                                Solicitud de Reactivación Registrada con Éxito
                            </div>
                            <p className="text-[11px] text-emerald-700 leading-relaxed">
                                Se ha generado el ticket de Control Dual con ID <code className="font-mono text-[10px] bg-white px-1 py-0.5 rounded">{requestSuccess.id.slice(0, 8)}</code>. Notifique a otro SuperAdmin de la plataforma para que apruebe la solicitud en el panel de seguridad.
                            </p>
                            <p className="text-[10px] text-emerald-600">
                                Una vez aprobada, dispondrá de una ventana improrrogable de 24 horas para configurar su Passkey o Authenticator.
                            </p>

                            <div className="pt-2 flex justify-end">
                                <button
                                    type="button"
                                    onClick={handleExecuteLogout}
                                    className="px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs cursor-pointer shadow-xs"
                                >
                                    Entendido, Salir al Login
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
